import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Wind, Activity, Sparkles, Loader2, Download, RotateCcw } from 'lucide-react';

import { API_URL } from '../config';

export default function VMPage() {
  const { token } = useAuth();
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('vm-data');
      return saved ? JSON.parse(saved) : {
        pathologie: '',
        poids: '',
        parametres: { 
          vt: '6', 
          pep: '5', 
          fr: '15', 
          fio2: '100',
          ie: '1/2',
          debit: '60',
          trigger: '3-5'
        }
      };
    } catch {
      return {
        pathologie: '',
        poids: '',
        parametres: { 
          vt: '6', 
          pep: '5', 
          fr: '15', 
          fio2: '100',
          ie: '1/2',
          debit: '60',
          trigger: '3-5'
        }
      };
    }
  });

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      const response = await fetch(`${API_URL}/api/ai/analyze-vm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Erreur');
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('OK');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="p-3 md:p-6 space-y-3 max-w-4xl mx-auto">
      <div className="flex justify-between items-start gap-2">
        <div className="flex items-center gap-2">
          <Wind className="w-6 h-6 text-blue-400" />
          <div>
            <h1 className="text-lg md:text-2xl font-bold text-white">VM</h1>
            <p className="text-xs text-gray-400">Ventilation Mecanique</p>
          </div>
        </div>
        <Button onClick={handleAnalyze} disabled={isAnalyzing} size="sm">
          {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        </Button>
      </div>

      <Tabs defaultValue="reglages">
        <TabsList className="grid w-full grid-cols-4 bg-gray-800">
          <TabsTrigger value="reglages" className="text-xs">Reglages</TabsTrigger>
          <TabsTrigger value="pathologies" className="text-xs">Pathologies</TabsTrigger>
          <TabsTrigger value="optimisation" className="text-xs">Optimisation</TabsTrigger>
          <TabsTrigger value="sedation" className="text-xs">Sedation</TabsTrigger>
        </TabsList>

        <TabsContent value="reglages" className="space-y-3 mt-3">
          <Card className="border-cyan-500/30">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-cyan-300">REGLAGES CLASSIQUES (Protection)</CardTitle>
              <p className="text-xs text-gray-400 mt-1">Mode: VS-AI-PEP</p>
            </CardHeader>
            <CardContent className="p-3 space-y-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Poids theorique (kg)</label>
                <Input type="number" value={formData.poids} onChange={(e) => setFormData({...formData, poids: e.target.value})} className="h-9" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Vt (ml/kg)</label>
                  <Input type="number" value={formData.parametres.vt} onChange={(e) => setFormData({...formData, parametres: {...formData.parametres, vt: e.target.value}})} className="h-9" />
                  <p className="text-xs text-green-400 mt-0.5">6-8 ml/kg</p>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">PEP (cmH2O)</label>
                  <Input type="number" value={formData.parametres.pep} onChange={(e) => setFormData({...formData, parametres: {...formData.parametres, pep: e.target.value}})} className="h-9" />
                  <p className="text-xs text-green-400 mt-0.5">5 cmH2O</p>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">FR (/min)</label>
                  <Input type="number" value={formData.parametres.fr} onChange={(e) => setFormData({...formData, parametres: {...formData.parametres, fr: e.target.value}})} className="h-9" />
                  <p className="text-xs text-green-400 mt-0.5">12-20/min</p>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">FiO2 (%)</label>
                  <Input type="number" value={formData.parametres.fio2} onChange={(e) => setFormData({...formData, parametres: {...formData.parametres, fio2: e.target.value}})} className="h-9" />
                  <p className="text-xs text-green-400 mt-0.5">95-99%</p>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">I/E</label>
                  <Input value={formData.parametres.ie} onChange={(e) => setFormData({...formData, parametres: {...formData.parametres, ie: e.target.value}})} className="h-9" placeholder="1/2" />
                  <p className="text-xs text-green-400 mt-0.5">1/2</p>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Debit (l/min)</label>
                  <Input type="number" value={formData.parametres.debit} onChange={(e) => setFormData({...formData, parametres: {...formData.parametres, debit: e.target.value}})} className="h-9" />
                  <p className="text-xs text-green-400 mt-0.5">60 l/min</p>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Trigger (l/min)</label>
                  <Input value={formData.parametres.trigger} onChange={(e) => setFormData({...formData, parametres: {...formData.parametres, trigger: e.target.value}})} className="h-9" placeholder="3-5" />
                  <p className="text-xs text-green-400 mt-0.5">3-5 l/min</p>
                </div>
              </div>
              {formData.poids && (
                <Alert className="bg-cyan-500/10 border-cyan-500/30 p-2">
                  <Activity className="h-4 w-4" />
                  <AlertDescription className="text-xs text-cyan-300">
                    Volume courant calcule: <strong>{Math.round(parseFloat(formData.poids) * parseFloat(formData.parametres.vt))} ml</strong>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pathologies" className="space-y-3 mt-3">
          <div className="space-y-3">
            {[
              { id: 'oap', nom: 'OAP', params: 'Debit 60 | FiO2 95-97% | PEP progressive 10 | FR 15-25 | Trigger 3-5 | I/E 1/2 a 1/3' },
              { id: 'bpco', nom: 'BPCO/ASTHME', params: 'Debit 60-80 | FiO2 88-92% | PEP 0-3 | FR 8-14 | Trigger 3-5 | I/E 1/3 a 1/5' },
              { id: 'sdra', nom: 'SDRA', params: 'PEP recrutement 30 ou 40 | FR normocapnie | Trigger 3-5 | I/E 1/2 a 1/3 | Vt 6 ml/kg' },
              { id: 'pneumo', nom: 'PNEUMOTHORAX', params: 'PEP = 0 cmH2O' }
            ].map((path) => (
              <button
                key={path.id}
                onClick={() => setFormData({...formData, pathologie: path.id})}
                className={`w-full text-left p-3 rounded border transition-all ${
                  formData.pathologie === path.id ? 'bg-blue-500/20 border-blue-500' : 'bg-gray-800 border-gray-700'
                }`}
              >
                <div className="font-medium text-sm text-white mb-1">{path.nom}</div>
                <div className="text-xs text-gray-400">{path.params}</div>
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="optimisation" className="space-y-3 mt-3">
          <Card className="border-purple-500/30">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-purple-300">OPTIMISATION VENTILATION</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-3">
              <div className="p-3 rounded bg-gray-800 space-y-2">
                <div className="text-xs font-semibold text-purple-300">MANOEUVRE RECRUTEMENT</div>
                <div className="text-xs text-gray-400">PEP 30 ou 40 cmH2O (selon pathologie)</div>
              </div>
              <div className="p-3 rounded bg-gray-800 space-y-2">
                <div className="text-xs font-semibold text-cyan-300">OBJECTIFS</div>
                <div className="text-xs text-gray-400 space-y-1">
                  <div>• SpO2 &gt; 92% (ou 88-92% si BPCO)</div>
                  <div>• EtCO2 35-45 mmHg (normocapnie)</div>
                  <div>• Vt 6-8 ml/kg (protection)</div>
                  <div>• Pplat &lt; 30 cmH2O</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sedation" className="space-y-3 mt-3">
          <Card className="border-purple-500/30">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-purple-300">SEDATION & CURARISATION</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-3">
              <div className="p-3 rounded bg-purple-500/10 border border-purple-500/30">
                <div className="text-xs font-semibold text-purple-300 mb-2">OPTION 1: PROPOFOL</div>
                <div className="text-xs text-gray-300">3-4 mg/kg/h IVSE</div>
              </div>
              <div className="p-3 rounded bg-purple-500/10 border border-purple-500/30">
                <div className="text-xs font-semibold text-purple-300 mb-2">OPTION 2: HYPNOVEL + SUFENTANIL</div>
                <div className="text-xs text-gray-300 space-y-1">
                  <div>• Hypnovel: 0,15-0,25 mg/kg/h</div>
                  <div>• Sufentanil: 0,2-2 ug/kg/h</div>
                </div>
              </div>
              <div className="p-3 rounded bg-amber-500/10 border border-amber-500/30">
                <div className="text-xs font-semibold text-amber-300 mb-2">CURARISATION (si besoin)</div>
                <div className="text-xs text-gray-300">Atracurium: 0,5 mg/kg</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {aiAnalysis && (
        <Card>
          <CardHeader className="p-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4" />ANALYSE IA
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xs">{aiAnalysis.analysis}</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
